/**
 * @fileoverview Quantity stepper Web Component with two auto-detected modes.
 *
 * Deliberately renders in the light DOM — unlike drawer/modal/popover, this element sits
 * inline inside themed content and must inherit theme typography, colour and borders. It
 * injects one shared <style> block for functional CSS only (layout reset for the number
 * input); all visual styling belongs to the theme, same as component-tabs.js.
 *
 * Hooks are `data-*` attributes, never CSS classes, so restyling can never break behaviour.
 *
 * Mode is inferred from `data-line`, the same trick snippets/product-swatches.liquid uses
 * with its `data-pdp` attribute:
 *
 *   data-line present → CART mode.  Writes through to the cart: on change it dispatches
 *                       `cart:change` on `document` with `{ line, quantity }`, debounced
 *                       (default 300ms) so holding "+" sends one request, not ten.
 *   data-line absent  → LOCAL mode. Holds a value only. The add-to-cart button reads
 *                       `.value` off the element when it is ready to submit.
 *
 * Both modes always dispatch `quantity:change` with `{ value, previousValue }`.
 *
 * @example
 * // Cart mode — has a line number, writes through to the cart
 * <quantity-picker-container data-line="1" data-min="1">
 *   <button type="button" data-quantity-decrease aria-label="Decrease quantity">&minus;</button>
 *   <input data-quantity-input type="number" inputmode="numeric" value="1" min="1">
 *   <button type="button" data-quantity-increase aria-label="Increase quantity">+</button>
 * </quantity-picker-container>
 *
 * @example
 * // Local mode — no line, just holds a value for add-to-cart
 * <quantity-picker-container data-min="1" data-max="10">
 *   <button type="button" data-quantity-decrease aria-label="Decrease quantity">&minus;</button>
 *   <input data-quantity-input type="number" inputmode="numeric" value="1" min="1">
 *   <button type="button" data-quantity-increase aria-label="Increase quantity">+</button>
 * </quantity-picker-container>
 *
 * @example
 * // Public API
 * const picker = document.querySelector('quantity-picker-container');
 * picker.value;      // → 1
 * picker.value = 3;  // sets, clamps and dispatches quantity:change
 *
 * picker.addEventListener('quantity:change', (e) => {
 *   console.log(e.detail.value, e.detail.previousValue);
 * });
 *
 * @attributes
 *   data-line      number   Cart line index. Present → cart mode, absent → local mode.
 *   data-min       number   Lower clamp bound  (default: 1)
 *   data-max       number   Upper clamp bound  (default: none)
 *   data-debounce  number   Cart-mode write-through delay in ms (default: 300)
 */

// Functional styles only — visual styling is left to the theme
if (!document.getElementById('component-quantity-picker-styles')) {
  const style = document.createElement('style');
  style.id = 'component-quantity-picker-styles';
  style.textContent = `
    quantity-picker-container {
      display: inline-flex;
      align-items: stretch;
    }

    quantity-picker-container [data-quantity-input] {
      text-align: center;
      -moz-appearance: textfield;
      appearance: textfield;
    }

    quantity-picker-container [data-quantity-input]::-webkit-outer-spin-button,
    quantity-picker-container [data-quantity-input]::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  `;
  document.head.appendChild(style);
}

const DEFAULT_DEBOUNCE = 300;

class QuantityPickerContainer extends HTMLElement {
  /** @type {HTMLInputElement|null} */
  #input = null;
  /** @type {HTMLElement|null} */
  #decrease = null;
  /** @type {HTMLElement|null} */
  #increase = null;

  /** Last value known to be valid — the fallback when input is emptied or garbled */
  #value = 1;
  /** @type {number|undefined} */
  #writeTimer = undefined;
  /** True while a debounced cart write is scheduled but not yet dispatched */
  #writePending = false;

  #boundDecrease = null;
  #boundIncrease = null;
  #boundInput = null;
  #boundBlur = null;
  #boundKeydown = null;

  connectedCallback() {
    this.#input = this.querySelector('[data-quantity-input]');
    this.#decrease = this.querySelector('[data-quantity-decrease]');
    this.#increase = this.querySelector('[data-quantity-increase]');

    // Without an input there is no value to hold — nothing else can work
    if (!this.#input) return;

    this.#value = this.#clamp(this.#parse(this.#input.value, this.#min));
    this.#input.value = String(this.#value);
    this.#syncButtons();

    this.#boundDecrease = () => this.#step(-1);
    this.#boundIncrease = () => this.#step(1);
    this.#boundInput = () => this.#onInput();
    this.#boundBlur = () => this.#onBlur();
    this.#boundKeydown = (e) => this.#onKeydown(e);

    this.#decrease?.addEventListener('click', this.#boundDecrease);
    this.#increase?.addEventListener('click', this.#boundIncrease);
    this.#input.addEventListener('input', this.#boundInput);
    this.#input.addEventListener('blur', this.#boundBlur);
    this.#input.addEventListener('keydown', this.#boundKeydown);
  }

  disconnectedCallback() {
    // Flush before teardown: a cart re-render can disconnect this element while a debounced
    // write is still scheduled, and dropping it would lose the user's last change.
    this.#flushWrite();
    clearTimeout(this.#writeTimer);
    this.#decrease?.removeEventListener('click', this.#boundDecrease);
    this.#increase?.removeEventListener('click', this.#boundIncrease);
    this.#input?.removeEventListener('input', this.#boundInput);
    this.#input?.removeEventListener('blur', this.#boundBlur);
    this.#input?.removeEventListener('keydown', this.#boundKeydown);
  }

  // ── Public API ────────────────────────────────────────────

  /** @returns {number} The current quantity */
  get value() {
    return this.#value;
  }

  /** @param {number|string} next - New quantity; clamped to data-min / data-max */
  set value(next) {
    this.#commit(this.#clamp(this.#parse(next, this.#value)));
  }

  /**
   * Sets the displayed quantity from an authoritative external source (the cart re-render)
   * WITHOUT dispatching `quantity:change` or scheduling a cart write — otherwise reconciling
   * the server's value would loop straight back into another mutation.
   *
   * No-op while a write of this picker's own value is still pending: the field then holds the
   * user's latest intent, which hasn't reached the server yet, so the server's older value
   * must not clobber it.
   *
   * @param {number|string} next
   */
  syncValue(next) {
    if (this.#writePending) return;
    const clamped = this.#clamp(this.#parse(next, this.#value));
    this.#value = clamped;
    if (this.#input) this.#input.value = String(clamped);
    this.#syncButtons();
  }

  /** @returns {boolean} True when this picker writes through to the cart */
  get isCartMode() {
    return this.hasAttribute('data-line') && this.getAttribute('data-line') !== '';
  }

  /** @returns {number|null} The cart line this picker controls, or null in local mode */
  get line() {
    if (!this.isCartMode) return null;
    const line = Number.parseInt(this.getAttribute('data-line'), 10);
    return Number.isFinite(line) ? line : null;
  }

  // ── Bounds ────────────────────────────────────────────────

  get #min() {
    const min = Number.parseInt(this.getAttribute('data-min') ?? '', 10);
    return Number.isFinite(min) ? min : 1;
  }

  get #max() {
    const max = Number.parseInt(this.getAttribute('data-max') ?? '', 10);
    return Number.isFinite(max) && max > 0 ? max : Infinity;
  }

  get #debounce() {
    const ms = Number.parseInt(this.getAttribute('data-debounce') ?? '', 10);
    return Number.isFinite(ms) && ms >= 0 ? ms : DEFAULT_DEBOUNCE;
  }

  // ── Internals ─────────────────────────────────────────────

  /**
   * Parses user input, rejecting anything non-numeric rather than letting NaN through.
   * @param {unknown} raw
   * @param {number} fallback - Returned when raw is empty or not a number
   * @returns {number}
   */
  #parse(raw, fallback) {
    const parsed = Number.parseInt(String(raw).trim(), 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  /** @param {number} value @returns {number} value clamped to data-min / data-max */
  #clamp(value) {
    return Math.min(Math.max(value, this.#min), this.#max);
  }

  /** @param {number} delta */
  #step(delta) {
    this.#commit(this.#clamp(this.#value + delta));
  }

  /**
   * Applies a new value to the input, notifies listeners, and — in cart mode — schedules
   * the debounced write-through. The value is applied optimistically so the UI stays
   * responsive; cart.js serialises the resulting requests and re-renders from the server.
   * @param {number} next
   * @param {boolean} syncField - Whether to write next back into the input. False while
   *   typing, so an in-progress entry like "01" isn't rewritten under the caret.
   */
  #commit(next, syncField = true) {
    const previousValue = this.#value;
    if (next === previousValue) {
      // Still resync the field: the user may have typed "1abc" over a value of 1
      if (syncField && this.#input && this.#input.value !== String(next)) {
        this.#input.value = String(next);
      }
      return;
    }

    this.#value = next;
    if (syncField && this.#input) this.#input.value = String(next);
    this.#syncButtons();

    this.dispatchEvent(
      new CustomEvent('quantity:change', {
        bubbles: true,
        detail: { value: next, previousValue },
      }),
    );

    if (this.isCartMode) this.#scheduleWrite();
  }

  /**
   * Debounces the cart write so a rapid click-through (five taps on "+") collapses into a
   * single `cart:change` carrying the final quantity, instead of five racing mutations.
   */
  #scheduleWrite() {
    if (this.line === null) return;

    clearTimeout(this.#writeTimer);
    this.#writePending = true;
    this.#writeTimer = setTimeout(() => this.#flushWrite(), this.#debounce);
  }

  /**
   * Dispatches the pending cart write now, if one is scheduled. Called both when the debounce
   * elapses and from disconnectedCallback — a re-render can disconnect this element mid-debounce,
   * and without flushing here the user's last increment would be silently dropped.
   */
  #flushWrite() {
    if (!this.#writePending) return;
    clearTimeout(this.#writeTimer);
    this.#writePending = false;

    const line = this.line;
    if (line === null) return;

    document.dispatchEvent(
      new CustomEvent('cart:change', {
        detail: { line, quantity: this.#value },
      }),
    );
  }

  /** Disables the steppers at their bounds so the affordance matches the clamp */
  #syncButtons() {
    if (this.#decrease) this.#decrease.disabled = this.#value <= this.#min;
    if (this.#increase) this.#increase.disabled = this.#value >= this.#max;
  }

  /**
   * Typing is intentionally permissive — an empty field mid-edit is normal. Only clearly
   * valid numbers commit here; anything else waits for blur.
   */
  #onInput() {
    if (!this.#input) return;

    const raw = this.#input.value.trim();
    if (raw === '') return;

    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return;

    // Only rewrite the field mid-typing when the clamp actually bit
    const clamped = this.#clamp(parsed);
    this.#commit(clamped, clamped !== parsed);
  }

  /** Never let the field sit empty or garbled — restore the last valid value */
  #onBlur() {
    if (!this.#input) return;
    this.#input.value = String(this.#value);
  }

  /** @param {KeyboardEvent} e */
  #onKeydown(e) {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.#step(1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.#step(-1);
    } else if (e.key === 'Enter') {
      // Commit immediately rather than waiting out the debounce
      e.preventDefault();
      this.#input?.blur();
    }
  }
}

customElements.define('quantity-picker-container', QuantityPickerContainer);
